import { Alert, Box, Button, Card, Divider, Modal, Text } from '@nimbus-ds/components';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type Corrida } from '../api';
import { Dato } from '../componentes/Dato';
import { Cargando, ErrorCaja, EtiquetaEstado } from '../componentes/Estado';
import { Layout } from '../componentes/Layout';
import { ModalDeshacer } from '../componentes/ModalDeshacer';
import { TablaItems } from '../componentes/TablaItems';
import { estadoDeshacer } from '../deshacer';
import { aErrorVisible, type ErrorVisible } from '../errores';
import { fecha, fechaHora, numero } from '../formato';

const EN_CURSO: ReadonlySet<Corrida['estado']> = new Set(['queued', 'running']);
const INTERVALO_MS = 2000;

export function DetalleCorrida(): ReactElement {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const [corrida, setCorrida] = useState<Corrida | null>(null);
  const [error, setError] = useState<ErrorVisible | null>(null);
  const [version, setVersion] = useState(0);
  const [deshaciendo, setDeshaciendo] = useState(false);
  /**
   * `true` con el modal de confirmación abierto.
   *
   * Acá el contexto ya es claro —se está mirando esa corrida y nada más— y podría no
   * llevar confirmación. Lleva igual: no existe deshacer del deshacer, y dos
   * comportamientos distintos para el mismo botón según desde dónde se aprieta es la clase
   * de inconsistencia que después nadie recuerda por qué existe.
   */
  const [confirmandoDeshacer, setConfirmandoDeshacer] = useState(false);
  const [avisoDeshacer, setAvisoDeshacer] = useState<string | null>(null);
  const [confirmandoFreno, setConfirmandoFreno] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const montado = useRef(true);

  const refrescar = useCallback(async (): Promise<Corrida | null> => {
    try {
      const { corrida: actual } = await api.corrida(id);
      if (!montado.current) return null;
      setCorrida(actual);
      setVersion((v) => v + 1);
      return actual;
    } catch (excepcion) {
      if (montado.current) {
        setError(aErrorVisible(excepcion, 'No pudimos cargar la actualización.'));
      }
      return null;
    }
  }, [id]);

  // Mientras la corrida está en curso se refresca sola, para mostrar el progreso en vivo.
  useEffect(() => {
    montado.current = true;
    let temporizador: ReturnType<typeof setTimeout> | undefined;

    const ciclo = async (): Promise<void> => {
      const actual = await refrescar();
      if (montado.current && actual !== null && EN_CURSO.has(actual.estado)) {
        temporizador = setTimeout(() => void ciclo(), INTERVALO_MS);
      }
    };
    void ciclo();

    return () => {
      montado.current = false;
      if (temporizador !== undefined) clearTimeout(temporizador);
    };
  }, [refrescar]);

  async function deshacer(): Promise<void> {
    setDeshaciendo(true);
    setError(null);
    try {
      const resultado = await api.deshacer(id);
      setConfirmandoDeshacer(false);
      setAvisoDeshacer(resultado.aviso);
      if (resultado.aRestaurar > 0) {
        navigate(`/corridas/${resultado.corridaId}`);
      } else {
        await refrescar();
      }
    } catch (excepcion) {
      setConfirmandoDeshacer(false);
      setError(aErrorVisible(excepcion, 'No pudimos deshacer la actualización.'));
    } finally {
      setDeshaciendo(false);
    }
  }

  async function cancelar(): Promise<void> {
    setCancelando(true);
    setError(null);
    try {
      await api.cancelar(id);
      setConfirmandoFreno(false);
      await refrescar();
    } catch (excepcion) {
      setError(aErrorVisible(excepcion, 'No pudimos frenar la actualización.'));
    } finally {
      setCancelando(false);
    }
  }

  if (corrida === null) {
    return (
      <Layout titulo="Actualización">
        {error === null ? <Cargando /> : <ErrorCaja mensaje={error.mensaje} codigo={error.codigo} />}
      </Layout>
    );
  }

  const enCurso = EN_CURSO.has(corrida.estado);
  const procesadas = corrida.totales.actualizadas + corrida.totales.fallidas;
  const objetivo = Math.max(1, corrida.totales.analizadas - corrida.totales.salteadas);
  const avance = Math.min(100, Math.round((procesadas / objetivo) * 100));
  const plazo = estadoDeshacer(corrida);
  const sePuedeDeshacer = plazo.clase === 'disponible';

  return (
    <Layout
      titulo={corrida.origen === 'undo' ? 'Deshacer actualización' : 'Actualización'}
    >
      {error !== null && <ErrorCaja mensaje={error.mensaje} codigo={error.codigo} />}

      {avisoDeshacer !== null && (
        <Alert appearance="warning" title="Hay precios que no revertimos">
          <Text>{avisoDeshacer}</Text>
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Box display="flex" flexDirection="column" gap="3">
            <Box display="flex" gap="2" alignItems="center" flexWrap="wrap">
              <EtiquetaEstado estado={corrida.estado} />
              <Text fontSize="caption" color="neutral-textLow">
                Empezó {fechaHora(corrida.iniciadaEn ?? corrida.creadaEn)}
                {corrida.terminadaEn === null ? '' : ` · Terminó ${fechaHora(corrida.terminadaEn)}`}
              </Text>
            </Box>

            {enCurso && (
              <Box display="flex" flexDirection="column" gap="1">
                <Text>
                  {numero(procesadas)} de {numero(objetivo)} variantes ({avance}%)
                  {/*
                    El porcentaje solo no alcanza: "38%" no dice si faltan dos minutos o
                    cuarenta. El texto lo arma el backend, que es el que sabe el ritmo real
                    de esta corrida, y viene en `null` cuando todavía no se puede decir nada
                    honesto. En ese caso queda el porcentaje, como antes.
                  */}
                  {corrida.restante === null ? '' : ` · ${corrida.restante.texto}`}
                </Text>
                <Text fontSize="caption" color="neutral-textLow">
                  Podés cerrar esta pantalla: la actualización sigue de todas formas.
                </Text>
              </Box>
            )}

            {!enCurso && corrida.estado !== 'cancelled' && (
              <Text>
                {corrida.estado === 'completed' || corrida.estado === 'completed_with_errors'
                  ? `Listo, actualizamos ${numero(corrida.totales.actualizadas)} precios.`
                  : ''}
                {corrida.totales.fallidas > 0
                  ? ` Quedaron ${numero(corrida.totales.fallidas)} sin actualizar.`
                  : ''}
              </Text>
            )}

            {corrida.estado === 'cancelled' && (
              <Text>
                {corrida.totales.actualizadas === 0
                  ? 'La frenaste antes de que cambiara ningún precio. No hay nada que revertir.'
                  : `La frenaste después de cambiar ${numero(corrida.totales.actualizadas)} precios. Esos quedaron cambiados en tu tienda: si no era lo que querías, deshacela.`}
              </Text>
            )}

            {/*
              Los mismos tres datos, con las mismas etiquetas, que en la tarjeta del
              historial. Una pantalla que cuenta lo mismo con otras palabras obliga a
              volver a leer.
            */}
            {!enCurso && (
              <>
                <Divider />
                <Box display="flex" gap={{ xs: '4', md: '6' }} flexWrap="wrap">
                  <Dato etiqueta="Variantes actualizadas">
                    {numero(corrida.totales.actualizadas)}
                  </Dato>
                  <Dato etiqueta="Sin cambios">{numero(corrida.totales.salteadas)}</Dato>
                  {corrida.totales.fallidas > 0 && (
                    <Dato etiqueta="Fallidas">{numero(corrida.totales.fallidas)}</Dato>
                  )}
                </Box>
              </>
            )}

            {corrida.error !== null && (
              <Alert appearance="danger" title="La actualización falló">
                <Text>{corrida.error}</Text>
              </Alert>
            )}

            {plazo.clase === 'disponible' && (
              <Text fontSize="caption" color="neutral-textLow">
                Podés deshacer esta actualización hasta el {fecha(corrida.sePuedeDeshacerHasta)}.
                Después de esa fecha ya no guardamos los precios anteriores.
              </Text>
            )}

            {plazo.clase === 'vencido' && (
              <Text fontSize="caption" color="neutral-textLow">
                Ya no se puede deshacer: el plazo {plazo.texto.toLowerCase()} y con él los precios
                anteriores que teníamos guardados.
              </Text>
            )}
          </Box>
        </Card.Body>
      </Card>

      {/*
        *** Las acciones van acá, después del estado, y no en la cabecera. ***

        Estaban arriba de todo: al entrar desde el celular, lo primero que quedaba bajo el
        pulgar era "Deshacer" — la única acción de la app sin vuelta atrás— antes de que la
        pantalla hubiera dicho una sola palabra sobre qué pasó. Primero se entiende, después
        se decide.

        Y entre ellas, las que tocan precios separadas de "Descargar CSV" por un renglón
        entero en el celular y por 16px en escritorio: el dedo o el cursor que va a bajar
        el CSV no puede caer en "Deshacer".
      */}
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap="2">
        <Box className="acciones-a-lo-ancho" display="flex">
          <Button onClick={() => void api.descargarCsv(corrida.id)}>Descargar CSV</Button>
        </Box>

        {(enCurso || sePuedeDeshacer) && (
          <Box
            className="acciones-a-lo-ancho"
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            gap="2"
            paddingTop={{ xs: '2', md: 'none' }}
            paddingLeft={{ xs: 'none', md: '4' }}
          >
            {enCurso && (
              <Button size="large" appearance="danger" onClick={() => setConfirmandoFreno(true)}>
                Frenar
              </Button>
            )}
            {sePuedeDeshacer && (
              <Button
                size="large"
                appearance="danger"
                disabled={deshaciendo}
                onClick={() => setConfirmandoDeshacer(true)}
              >
                {deshaciendo ? 'Deshaciendo…' : 'Deshacer'}
              </Button>
            )}
          </Box>
        )}
      </Box>

      <TablaItems corridaId={corrida.id} version={version} />

      <ModalDeshacer
        corrida={confirmandoDeshacer ? corrida : null}
        deshaciendo={deshaciendo}
        onCancelar={() => setConfirmandoDeshacer(false)}
        onConfirmar={() => void deshacer()}
      />

      <Modal open={confirmandoFreno} onDismiss={() => setConfirmandoFreno(false)}>
        <Modal.Header title="¿Frenar esta actualización?" />
        <Modal.Body padding="none">
          <Box display="flex" flexDirection="column" gap="2">
            <Text>
              Los {numero(corrida.totales.actualizadas)} precios que ya cambiamos quedan
              cambiados: frenar no los vuelve atrás. El resto no se toca.
            </Text>
            <Text fontSize="caption" color="neutral-textLow">
              Después vas a poder deshacerla desde acá mismo y volver todo como estaba.
            </Text>
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setConfirmandoFreno(false)}>Seguir actualizando</Button>
          <Button appearance="danger" disabled={cancelando} onClick={() => void cancelar()}>
            {cancelando ? 'Frenando…' : 'Sí, frenar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
}
